import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import dayjs, { Dayjs } from 'dayjs';
import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useClientStore } from '@core/client/useClientStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import {
  DashboardWorkerOnShiftResponse,
  DaySequence,
  DaySequenceStatus,
  Job,
  JobStatus,
} from '@core/generated/gql-types';
import { useTenantId } from '@/contexts/TenantContext';
import { useUiFiltersStore } from '@/stores/useUiFiltersStore';
import type { DashboardView } from '@/modules/dashboard/views/dashboard.view';
import { buildShiftsFromStores } from '@/modules/dashboard/utils/todaysJobs.utils';
import { doesJobWorkInDateRange } from '@/modules/jobs/utils/jobFilters.utils';
import { getPriorPeriod } from '@/modules/dashboard/utils/priorPeriod.utils';

/** Safety cap so a misbehaving cursor can't loop forever. */
const MAX_JOB_PAGES = 50;

const buildDefaultDashboardRange = () => ({
  startIso: dayjs().startOf('day').toISOString(),
  endIso: dayjs().add(6, 'day').endOf('day').toISOString(),
});

type UseDashboardData = {
  isLoading: boolean;
  jobs: Job[];
  timeframeStartDate: Dayjs;
  timeframeEndDate: Dayjs;
  handleDateChange: (start: Dayjs, end: Dayjs) => void;
  refreshData: () => Promise<void>;
  filledJobs: Job[];
  timeframeUnfilledJobs: Job[];
  unfilledJobs: Job[];
  disputedTimesheets: DaySequence[];
  todaysShifts: DashboardWorkerOnShiftResponse[];
};

export const useDashboardData = (view: DashboardView): UseDashboardData => {
  const tenantId = useTenantId();
  const { fetchWorkersForTenant, workers } = useWorkerStore();
  const { jobs, fetchAllJobsForTenantByDateRange, getJobsByStatus } =
    useJobStore();
  const { fetchClientsForTenant } = useClientStore();
  const { byId, fetchAllDaySequencesRequiringAction } = useDaySequenceStore();
  const [isLoading, setIsLoading] = useState(false);
  const todayCalendarDate = dayjs().format('YYYY-MM-DD');
  const todaysShifts = useMemo(
    () =>
      buildShiftsFromStores({
        tenantId,
        jobs,
        daySequences: Object.values(byId),
        workers,
        calendarDate: todayCalendarDate,
      }),
    [tenantId, jobs, byId, workers, todayCalendarDate],
  );
  const dashboardDateRange = useUiFiltersStore(
    (state) => state.dashboardDateRange,
  );
  const setDashboardDateRange = useUiFiltersStore(
    (state) => state.setDashboardDateRange,
  );

  // Build defaults once per mount so the seed-effect and fallback share the same ISO strings,
  // avoiding a double fetch on first visit.
  const fallbackRangeRef = useRef(buildDefaultDashboardRange());

  useEffect(() => {
    if (!dashboardDateRange) {
      setDashboardDateRange(fallbackRangeRef.current);
    }
  }, [dashboardDateRange, setDashboardDateRange]);

  const activeStartIso =
    dashboardDateRange?.startIso ?? fallbackRangeRef.current.startIso;
  const activeEndIso =
    dashboardDateRange?.endIso ?? fallbackRangeRef.current.endIso;

  const timeframeStartDate = useMemo<Dayjs>(
    () => dayjs(activeStartIso),
    [activeStartIso],
  );
  const timeframeEndDate = useMemo<Dayjs>(
    () => dayjs(activeEndIso),
    [activeEndIso],
  );
  const priorPeriodStart = useMemo(
    () => getPriorPeriod(timeframeStartDate, timeframeEndDate).start,
    [timeframeStartDate, timeframeEndDate],
  );

  const timeframeFilteredJobs = useMemo(() => {
    return jobs.filter((job) =>
      doesJobWorkInDateRange(
        job,
        timeframeStartDate.toDate(),
        timeframeEndDate.toDate(),
      ),
    );
  }, [jobs, timeframeStartDate, timeframeEndDate]);

  const filledJobs = useMemo(() => {
    return timeframeFilteredJobs.filter(
      (job) =>
        job.itemStatus === JobStatus.Filled ||
        job.itemStatus === JobStatus.Completed,
    );
  }, [timeframeFilteredJobs]);

  const timeframeUnfilledJobs = useMemo(() => {
    return timeframeFilteredJobs.filter(
      (job) => job.itemStatus === JobStatus.Unfilled,
    );
  }, [timeframeFilteredJobs]);

  const unfilledJobs = getJobsByStatus([JobStatus.Unfilled]);

  const disputedTimesheets = useMemo(() => {
    const allDaySequences = Object.values(byId);
    return allDaySequences.filter(
      (daySequence) =>
        daySequence.status === DaySequenceStatus.EscalatedToTenant,
    );
  }, [byId]);

  const handleDateChange = useCallback(
    (start: Dayjs, end: Dayjs) => {
      const nextStartIso = start.startOf('day').toISOString();
      const nextEndIso = end.endOf('day').toISOString();
      if (nextStartIso === activeStartIso && nextEndIso === activeEndIso) {
        return;
      }

      setDashboardDateRange({
        startIso: nextStartIso,
        endIso: nextEndIso,
      });
    },
    [activeStartIso, activeEndIso, setDashboardDateRange],
  );

  const fetchAllJobPagesForTimeframe = useCallback(
    async (currentTenantId: string, dateFrom: string, dateTo: string) => {
      let nextKey: string | undefined;
      for (let page = 0; page < MAX_JOB_PAGES; page += 1) {
        const jobsResponse = await fetchAllJobsForTenantByDateRange(
          currentTenantId,
          dateFrom,
          dateTo,
          undefined,
          undefined,
          nextKey,
        );
        if (!jobsResponse.hasMore || !jobsResponse.lastEvaluatedKey) return;
        nextKey = jobsResponse.lastEvaluatedKey;
      }
    },
    [fetchAllJobsForTenantByDateRange],
  );

  const refreshData = useCallback(async () => {
    if (!tenantId) return;

    setIsLoading(true);
    try {
      const fetchTasks: Promise<unknown>[] = [];

      if (view.query.clients.fetch) {
        fetchTasks.push(
          fetchClientsForTenant(tenantId, undefined, undefined, true),
        );
      }
      if (view.query.workers.fetch) {
        fetchTasks.push(fetchWorkersForTenant(tenantId));
      }
      if (view.query.daySequencesRequiringAction.fetch) {
        fetchTasks.push(fetchAllDaySequencesRequiringAction(tenantId));
      }
      if (view.query.jobs.strategy === 'tenantByDateRange') {
        fetchTasks.push(
          fetchAllJobPagesForTimeframe(
            tenantId,
            priorPeriodStart.toISOString(),
            timeframeEndDate.toISOString(),
          ),
        );
      }

      await Promise.all(fetchTasks);
    } catch (error) {
      const errorString = `Error initializing data for the dashboard. `;

      console.error(errorString, error);

      const toastError =
        error instanceof Error ? error.message : 'An unexpected error occurred';

      toast.error(toastError, {
        position: 'bottom-left',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    tenantId,
    view.query.clients.fetch,
    view.query.workers.fetch,
    view.query.daySequencesRequiringAction.fetch,
    view.query.jobs.strategy,
    fetchClientsForTenant,
    fetchWorkersForTenant,
    fetchAllDaySequencesRequiringAction,
    fetchAllJobPagesForTimeframe,
    priorPeriodStart,
    timeframeEndDate,
  ]);

  useEffect(() => {
    if (tenantId) {
      refreshData();
    }
  }, [tenantId, refreshData]);

  return {
    isLoading,
    jobs,
    timeframeStartDate,
    timeframeEndDate,
    handleDateChange,
    refreshData,
    filledJobs,
    timeframeUnfilledJobs,
    unfilledJobs,
    disputedTimesheets,
    todaysShifts,
  };
};
